import { useGetScoreListQuery } from '@fiji/common/src/features/prediction/predictionApi';
import { Stack, Avatar, Typography, List, ListItem, Box } from '@mui/material';
import { CustomModal, CustomTransComponent } from 'components';
import { ModalActions } from 'components/CustomModal';
import React, { type JSX } from 'react';
import { UPCOMING_EVENTS_DETAILS_TEST_IDS } from 'pages/UpcomingEventsDetails/testIds';

export const ScoreModal = React.forwardRef((props: any, ref): JSX.Element => {
    const [openPulseScore, setOpenPulseScore] = React.useState(false);
    const { data: scoreListData, isLoading }: any = useGetScoreListQuery(undefined, { skip: !openPulseScore });
    const pulseScoreActions: ModalActions[] = [
        {
            key: 'ok',
            label: <CustomTransComponent translationKey="COMMON:OK" />,
            close: true,
            variant: 'contained',
            handleClick: (): void => {
                setOpenPulseScore(false);
            },
        },
    ];

    React.useImperativeHandle(ref, () => ({
        onClick: (): void => setOpenPulseScore(true),
    }));

    return (
        <CustomModal
            isOpen={openPulseScore}
            isLoading={isLoading}
            noDividers
            hidePadding
            type="medium"
            actions={pulseScoreActions}
            header={<CustomTransComponent translationKey="UPCOMING_EVENTS:PULSE_SCORE" />}
            data-testid={props?.['data-testid'] ?? UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.root}
            aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.root}
        >
            <>
                <Stack
                    px={3}
                    data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.gradesRow}
                    aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.gradesRow}
                >
                    <Stack direction={'row'} spacing={1} py={1}>
                        {scoreListData?.data?.grades?.map(
                            (item: any): JSX.Element => (
                                <Avatar
                                    key={item?.gradeName + item?.color}
                                    sx={{ backgroundColor: item?.color ?? '#2CA618' }}
                                    aria-label={`score-grade-${String(item?.gradeName ?? '').toLowerCase()}`}
                                >
                                    <Typography
                                        className="text-white"
                                        fontWeight={'700'}
                                        fontSize={'20px'}
                                        textAlign={'center'}
                                    >
                                        {item?.gradeName}
                                    </Typography>
                                </Avatar>
                            )
                        )}
                    </Stack>
                    <Typography
                        variant="body1"
                        className="black-500"
                        mt={2}
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightedMethodText}
                        aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightedMethodText}
                    >
                        <CustomTransComponent
                            translationKey="UPCOMING_EVENTS:PULSE_SCORE_MODAL_WEIGHTED_METHOD"
                            components={{ bold: <span className="fw-700 f-16" /> }}
                        />
                    </Typography>
                </Stack>
                <List
                    disablePadding
                    className="margin-y-24"
                    data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightsList}
                    aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightsList}
                >
                    <ListItem
                        className="flex-center-between padding-x-24"
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.deviceModeItem}
                        aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.deviceModeItem}
                    >
                        <Typography variant="body1" className="black-500">
                            <CustomTransComponent translationKey="UPCOMING_EVENTS:PULSE_SCORE_MODAL_DEVICE_MODE" />
                        </Typography>
                        <Box className="border-1 border-radius-4 padding-4">
                            <Typography variant="body1">30</Typography>
                        </Box>
                    </ListItem>
                    <ListItem
                        className="flex-center-between bg-header padding-x-24"
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.eventsAndAlarmsItem}
                        aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.eventsAndAlarmsItem}
                    >
                        <Typography variant="body1" className="black-500">
                            <CustomTransComponent translationKey="UPCOMING_EVENTS:PULSE_SCORE_MODAL_EVENTS_AND_ALARMS" />
                        </Typography>
                        <Box className="border-1 border-radius-4 padding-4">
                            <Typography variant="body1">25</Typography>
                        </Box>
                    </ListItem>
                    <ListItem
                        className="flex-center-between padding-x-24"
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.deviceHealthDataItem}
                        aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.deviceHealthDataItem}
                    >
                        <Typography variant="body1" className="black-500">
                            <CustomTransComponent translationKey="UPCOMING_EVENTS:PULSE_SCORE_MODAL_DEVICE_HEALTH_DATA" />
                        </Typography>
                        <Box className="border-1 border-radius-4 padding-4">
                            <Typography variant="body1">20</Typography>
                        </Box>
                    </ListItem>
                    <ListItem
                        className="flex-center-between bg-header padding-x-24"
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.historicalDataItem}
                        aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.historicalDataItem}
                    >
                        <Typography variant="body1" className="black-500">
                            <CustomTransComponent translationKey="UPCOMING_EVENTS:PULSE_SCORE_MODAL_HISTORICAL_DATA" />
                        </Typography>
                        <Box className="border-1 border-radius-4 padding-4">
                            <Typography variant="body1">15</Typography>
                        </Box>
                    </ListItem>
                    <ListItem
                        className="flex-center-between padding-x-24"
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.configurationAgeItem}
                        aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.configurationAgeItem}
                    >
                        <Typography variant="body1" className="black-500">
                            <CustomTransComponent translationKey="UPCOMING_EVENTS:PULSE_SCORE_MODAL_CONFIGURATION_AGE_BEST_PRACTICES" />
                        </Typography>
                        <Box className="border-1 border-radius-4 padding-4">
                            <Typography variant="body1">10</Typography>
                        </Box>
                    </ListItem>
                </List>
                <Stack spacing={1} px={3}>
                    <Typography
                        variant="body1"
                        className="black-500"
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.refreshedText}
                        aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.refreshedText}
                    >
                        <CustomTransComponent translationKey="UPCOMING_EVENTS:PULSE_SCORE_MODAL_SCORING_REFRESHED" />
                    </Typography>
                    <Typography
                        variant="body1"
                        className="black-500"
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.devicesInNormalModeText}
                        aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.devicesInNormalModeText}
                    >
                        <CustomTransComponent translationKey="UPCOMING_EVENTS:PULSE_SCORE_MODAL_DEVICES_IN_NORMAL_MODE" />
                    </Typography>
                    <Typography
                        variant="body1"
                        className="black-500"
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.notAllDevicesSupportedText}
                        aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.notAllDevicesSupportedText}
                    >
                        <CustomTransComponent translationKey="UPCOMING_EVENTS:PULSE_SCORE_MODAL_NOT_ALL_DEVICES_SUPPORTED" />
                    </Typography>
                </Stack>
            </>
        </CustomModal>
    );
});
